import { useCallback, useEffect, useState } from "react";
import { RotationPicker } from "../RotationPicker";
import { gameName } from "../../lib/gameName";
import { Ban, Pencil, Play, Plus, Timer, Trophy, Users } from "lucide-react";
import { toast } from "sonner";
import { api, C, errorMessage, fmtChips } from "../../lib/api";

const input = "bg-black/40 border border-amber-400/20 rounded-lg px-2 py-1 text-xs text-amber-50 w-full";
const label = "text-[10px] uppercase tracking-[0.18em] text-slate-400";

const BLANK = {
  name: "", game: "HOLDEM", rotation: [], max_seats: 9, free: false, buy_in: 100, fee_percent: 10, prize_per_entrant: 0, prize_pool_fixed: 0,
  vip_top3: "", starting_stack: 5000, level_minutes: 5, late_reg_levels: 5, re_entries: 0, mode: "FREEZEOUT",
  rebuy_levels: 0, rebuy_cost: 0, rebuy_chips: 0, addon: false, addon_at_level: 6, addon_cost: 0, addon_chips: 0,
  break_every_levels: 4, break_minutes: 5, min_entrants: 2, bots: false, bot_count: 0, times: "20:00",
  affiliate_event: false, affiliate_id: "", affiliate_prefix: "", paid_places: 0, place_codes: [], generate: false,
};

const ordinal = (n) => `${n}${["th", "st", "nd", "rd"][n % 100 > 10 && n % 100 < 14 ? 0 : Math.min(n % 10, 4) % 4] || "th"}`;
const tailOf = (c) => String(c || "").split("-").pop().toUpperCase();
const cleanPrefix = (s) => String(s || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8);

// an affiliate satellite cannot be saved until every paid place has its own 8-character code
const affReady = (f) => {
  if (!f.affiliate_event) return true;
  const n = Number(f.paid_places) || 0;
  const codes = (f.place_codes || []).slice(0, n).map((c) => String(c || "").toUpperCase());
  return !!f.affiliate_id && !!f.affiliate_prefix && n >= 1 && codes.length === n
    && codes.every((c) => /^[A-Z0-9]{8}$/.test(c)) && new Set(codes).size === n;
};

// Tournaments tab: the scheduled MTTs and daily freerolls. Create, edit the numbers, open registration now, cancel.
export const MttPanel = () => {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [affs, setAffs] = useState([]);
  const [regsOpen, setRegsOpen] = useState(null);
  const load = useCallback(() => api.get(C.ApiRoutes.ADMIN_MTT).then((r) => setRows(r.data)).catch(() => {}), []);
  useEffect(() => { load(); const i = setInterval(load, 8000); return () => clearInterval(i); }, [load]);
  useEffect(() => { api.get("/api/admin/affiliates").then((r) => setAffs(r.data.affiliates)).catch(() => {}); }, []);
  const affName = (id) => affs.find((a) => a.affiliate_id === id)?.business_name || "";

  const setPlaces = (n) => setForm((f) => ({ ...f, paid_places: n, generate: false, place_codes: (f.place_codes || []).slice(0, n) }));
  const setCode = (i, v) => setForm((f) => {
    const list = [...(f.place_codes || [])];
    list[i] = v.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8);
    return { ...f, place_codes: list, generate: false };
  });
  const genCodes = async () => {
    try {
      const { data } = await api.post("/api/admin/mtt/generate-codes", null,
        { params: { affiliate_id: form.affiliate_id, count: Number(form.paid_places) } });
      setForm((f) => ({ ...f, generate: true, affiliate_prefix: cleanPrefix(f.affiliate_prefix || data.prefix), place_codes: data.codes.map(tailOf) }));
      toast.success(`${data.codes.length} codes generated`);
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const patch = async (ev, update) => {
    try { await api.patch(C.ApiRoutes.ADMIN_MTT_EVENT.replace("{event_id}", ev.event_id), update); await load(); }
    catch (e) { toast.error(errorMessage(e)); }
  };
  const hit = async (ev, route, ok, params) => {
    try { await api.post(route.replace("{event_id}", ev.event_id), null, { params }); toast.success(ok); await load(); }
    catch (e) { toast.error(errorMessage(e)); }
  };
  const create = async () => {
    setBusy(true);
    try {
      const f = form;
      const prefix = (f.affiliate_prefix || "").toUpperCase();
      const body = {
        ...f,
        vip_top3: String(f.vip_top3).split(/[,/\s]+/).filter(Boolean).map(Number).slice(0, 3),
        affiliate_event: !!f.affiliate_event,
        affiliate_id: f.affiliate_event ? f.affiliate_id : "",
        affiliate_prefix: f.affiliate_event ? prefix : "",
        paid_places: f.affiliate_event ? Number(f.paid_places) || 0 : 0,
        affiliate_codes: f.affiliate_event
          ? (f.place_codes || []).slice(0, Number(f.paid_places) || 0).map((t) => `${prefix}-${String(t || "").toUpperCase()}`)
          : [],
        schedule: { times: String(f.times).split(/[,\s]+/).filter(Boolean), tz_offset: 600, tz_label: "UTC+10" },
      };
      delete body.times;
      delete body._edit;
      delete body.place_codes;
      delete body.generate;
      ["max_seats", "buy_in", "fee_percent", "prize_per_entrant", "prize_pool_fixed", "starting_stack", "level_minutes", "late_reg_levels",
        "re_entries", "rebuy_levels", "rebuy_cost", "rebuy_chips", "addon_at_level", "addon_cost", "addon_chips",
        "break_every_levels", "break_minutes", "min_entrants", "bot_count"].forEach((k) => { body[k] = Number(body[k]) || 0; });
      body.min_entrants = Math.max(2, body.min_entrants);
      if (f._edit) {
        await api.patch(C.ApiRoutes.ADMIN_MTT_EVENT.replace("{event_id}", f._edit), body);
        toast.success("Tournament updated");
      } else {
        await api.post(C.ApiRoutes.ADMIN_MTT, body);
        toast.success("Tournament created");
      }
      setForm(null);
      await load();
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };

  const edit = (ev) => setForm(form?._edit === ev.event_id ? null : {
    ...BLANK, ...ev, _edit: ev.event_id,
    times: (ev.schedule?.times || []).join(" "),
    vip_top3: (ev.vip_top3 || []).join("/"),
    affiliate_event: !!ev.affiliate_event, affiliate_id: ev.affiliate_id || "", affiliate_prefix: ev.affiliate_prefix || "",
    paid_places: Number(ev.paid_places) || (ev.affiliate_codes || []).length,
    place_codes: (ev.affiliate_codes || []).map(tailOf), generate: false,
  });

  const Num = ({ k, l, ev }) => (
    <label className="flex flex-col gap-1"><span className={label}>{l}</span>
      <input data-testid={`mtt-f-${k}`} className={input} value={ev[k] ?? 0} onChange={(e) => setForm({ ...form, [k]: e.target.value })} /></label>
  );

  return (
    <div className="flex flex-col gap-5" data-testid="admin-mtt-panel">
      <div className="flex items-center justify-between">
        <h2 className="text-sm uppercase tracking-[0.2em] gold-text flex items-center gap-2"><Trophy className="w-4 h-4" /> Tournaments</h2>
        <button data-testid="mtt-new" onClick={() => setForm(form ? null : { ...BLANK })} className="btn-gold rounded-full px-4 py-1.5 text-xs font-display flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> New tournament</button>
      </div>

      {form && (
        <div className="gold-card rounded-xl p-5 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="mtt-create-form">
          <div className="col-span-2 md:col-span-4 text-[10px] uppercase tracking-[0.2em] text-amber-300/80" data-testid="mtt-form-title">
            {form._edit ? `Editing ${form.name} — changes apply to the next tournament that starts (a running one keeps its settings)` : "New tournament"}
          </div>
          <label className="flex flex-col gap-1 col-span-2"><span className={label}>Name</span>
            <input data-testid="mtt-f-name" className={input} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Sunday Major" /></label>
          <label className="flex flex-col gap-1"><span className={label}>Game</span>
            <select data-testid="mtt-f-game" className={input} value={form.game} onChange={(e) => setForm({ ...form, game: e.target.value })}>
              {Object.keys(C.GAME_RULES).map((g) => <option key={g} value={g}>{C.GAME_RULES[g].label}</option>)}
              <option value="ROTATION">ROTATION — mixed games</option>
            </select></label>
          {form.game === "ROTATION" && <RotationPicker className="col-span-4 text-amber-100" value={form.rotation || []} onChange={(rotation) => setForm({ ...form, rotation })} />}
          <label className="flex flex-col gap-1"><span className={label}>Start times (UTC+10)</span>
            <input data-testid="mtt-f-times" className={input} value={form.times} onChange={(e) => setForm({ ...form, times: e.target.value })} placeholder="11:00 20:00" /></label>
          <label className="flex items-center gap-2 text-xs text-amber-100 mt-5"><input data-testid="mtt-f-free" type="checkbox" checked={form.free} onChange={(e) => setForm({ ...form, free: e.target.checked })} /> Freeroll (free entry)</label>
          <Num k="buy_in" l="Buy-in" ev={form} /><Num k="fee_percent" l="Fee %" ev={form} />
          <Num k="prize_per_entrant" l="Freeroll prize / entrant" ev={form} />
          <Num k="prize_pool_fixed" l="Freeroll guaranteed pool" ev={form} />
          <Num k="vip_top3" l="VIP top 3 (e.g. 10/6/3)" ev={form} />
          <Num k="starting_stack" l="Starting stack" ev={form} /><Num k="level_minutes" l="Level minutes" ev={form} />
          <Num k="max_seats" l="Seats per table" ev={form} /><Num k="late_reg_levels" l="Late reg through level" ev={form} />
          <Num k="re_entries" l="Re-entries allowed" ev={form} />
          <label className="flex flex-col gap-1"><span className={label}>Mode</span>
            <select data-testid="mtt-f-mode" className={input} value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })}>
              <option value="FREEZEOUT">Freezeout</option><option value="REBUY">Re-buy</option>
            </select></label>
          <Num k="rebuy_levels" l="Re-buys through level" ev={form} /><Num k="rebuy_cost" l="Re-buy cost" ev={form} />
          <Num k="rebuy_chips" l="Re-buy chips" ev={form} />
          <label className="flex items-center gap-2 text-xs text-amber-100 mt-5"><input data-testid="mtt-f-addon" type="checkbox" checked={form.addon} onChange={(e) => setForm({ ...form, addon: e.target.checked })} /> Add-on offered</label>
          <Num k="addon_at_level" l="Add-on at level (break)" ev={form} /><Num k="addon_cost" l="Add-on cost" ev={form} />
          <Num k="addon_chips" l="Add-on chips" ev={form} />
          <Num k="break_every_levels" l="Break every N levels" ev={form} /><Num k="break_minutes" l="Break minutes" ev={form} />
          <Num k="min_entrants" l="Minimum entrants" ev={form} />
          <label className="flex items-center gap-2 text-xs text-amber-100 mt-5"><input data-testid="mtt-f-bots" type="checkbox" checked={form.bots} onChange={(e) => setForm({ ...form, bots: e.target.checked })} /> Practice bots fill the field</label>
          <Num k="bot_count" l="How many bots" ev={form} />
          <div className="col-span-2 md:col-span-4 mt-2 rounded-xl border border-amber-400/20 bg-black/30 p-4 flex flex-col gap-3" data-testid="mtt-affiliate-block">
            <label className="flex items-center gap-2 text-xs text-amber-100">
              <input data-testid="mtt-f-affiliate-event" type="checkbox" checked={!!form.affiliate_event}
                onChange={(e) => setForm({ ...form, affiliate_event: e.target.checked, generate: false })} />
              <span className="font-display tracking-wider">Affiliate event</span>
              <span className="text-[10px] text-slate-400 normal-case">a club's satellite — they sign off the buy-in, date, blinds and winner codes before players see it</span>
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <label className="flex flex-col gap-1"><span className={label}>Affiliate club</span>
                <select data-testid="mtt-f-affiliate" className={`${input} disabled:opacity-40`} disabled={!form.affiliate_event} value={form.affiliate_id}
                  onChange={(e) => {
                    const a = affs.find((x) => x.affiliate_id === e.target.value);
                    setForm({ ...form, affiliate_id: e.target.value, affiliate_prefix: a ? cleanPrefix(a.code_prefix || a.referral_code) : "", place_codes: [], generate: false });
                  }}>
                  <option value="">— choose a club —</option>
                  {affs.map((a) => <option key={a.affiliate_id} value={a.affiliate_id}>{a.business_name}</option>)}
                </select></label>
              <label className="flex flex-col gap-1"><span className={label}>Winner-code prefix</span>
                <input data-testid="mtt-f-affiliate-prefix" className={`${input} disabled:opacity-40`} disabled={!form.affiliate_event}
                  value={form.affiliate_prefix} placeholder="BJPG"
                  onChange={(e) => setForm({ ...form, affiliate_prefix: cleanPrefix(e.target.value) })} /></label>
              <label className="flex flex-col gap-1"><span className={label}>How many players get paid?</span>
                <select data-testid="mtt-f-paid-places" className={`${input} disabled:opacity-40`} disabled={!form.affiliate_event}
                  value={form.paid_places || 0} onChange={(e) => setPlaces(Number(e.target.value))}>
                  <option value={0}>— choose —</option>
                  {Array.from({ length: 20 }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
                </select></label>
              <label className="flex items-center gap-2 text-xs text-amber-100 mt-5">
                <input data-testid="mtt-f-generate" type="checkbox" disabled={!form.affiliate_event || !form.affiliate_id || !form.paid_places}
                  checked={!!form.generate} onChange={(e) => (e.target.checked ? genCodes() : setForm({ ...form, generate: false, place_codes: [] }))} />
                GENERATE codes
              </label>
            </div>
            {form.affiliate_event && !!form.paid_places && (
              <div className="flex flex-col gap-2" data-testid="mtt-prize-codes">
                <div className={label}>Prizes — one redemption code per paid place ({form.generate ? "generated" : "8 characters each, as supplied by the club"})</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {Array.from({ length: Number(form.paid_places) }, (_, i) => (
                    <div key={i} className="flex items-center gap-1">
                      <span className="w-8 text-[11px] text-amber-300 font-display">{ordinal(i + 1)}</span>
                      <span className="text-[11px] font-mono text-amber-200/80">{form.affiliate_prefix || "?"}-</span>
                      <input data-testid={`mtt-f-code-${i + 1}`} className={`${input} font-mono tracking-widest`} maxLength={8}
                        value={(form.place_codes || [])[i] || ""} placeholder="XXXXXXXX"
                        onChange={(e) => setCode(i, e.target.value)} />
                    </div>
                  ))}
                </div>
                <div className="text-[10px] text-slate-500">Each code is the club's prefix then 8 characters. Tick GENERATE and we make them; otherwise type in the codes the club gave us. They all have to be different.</div>
              </div>
            )}
          </div>
          <div className="col-span-2 md:col-span-4 flex gap-2 justify-end">
            <button onClick={() => setForm(null)} className="rounded-full px-4 py-1.5 text-xs bg-white/5">Cancel</button>
            <button data-testid="mtt-create" disabled={busy || !form.name || !affReady(form)} onClick={create} className="btn-gold rounded-full px-5 py-1.5 text-xs font-display disabled:opacity-40">{form._edit ? "Save changes" : "Create"}</button>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-3">
        {rows.map((ev) => (
          <div key={ev.event_id} data-testid={`admin-mtt-${ev.event_id}`} className="gold-row rounded-xl px-5 py-4 flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="font-display text-sm text-amber-50 flex items-center gap-2">{ev.name}
                {ev.free && <span className="rounded-full border border-emerald-400/60 px-2 py-0.5 text-[9px] tracking-[0.2em] text-emerald-200">FREEROLL</span>}
                <span className={`rounded-full px-2 py-0.5 text-[9px] tracking-[0.2em] ${ev.status === "open" ? "text-emerald-300 border border-emerald-400/50" : "text-slate-400 border border-slate-500/40"}`}>{ev.status.toUpperCase()}</span>
              </div>
              <div className="text-xs text-slate-400 mt-1 font-mono flex flex-wrap gap-x-3">
                <span>{gameName(ev)}</span>
                <span>{ev.free ? `prize ${fmtChips(ev.prize_pool_fixed)} + ${fmtChips(ev.prize_per_entrant)}/entrant` : `${fmtChips(ev.buy_in)} + ${ev.fee_percent}%`}</span>
                <span>{fmtChips(ev.starting_stack)} stack · {ev.level_minutes}-min · {ev.max_seats}-max</span>
                <span>late reg L{ev.late_reg_levels} · {ev.mode === "REBUY" ? `re-buys to L${ev.rebuy_levels}` : "freezeout"}{ev.addon ? ` · add-on L${ev.addon_at_level}` : ""}</span>
                <span>{(ev.schedule?.times || []).join(" · ")} {ev.schedule?.tz_label}</span>
                {ev.bots && <span className="text-amber-300">{ev.bot_count} practice bots</span>}
                {ev.affiliate_id && <span className="text-amber-200" data-testid={`admin-mtt-aff-tag-${ev.event_id}`}>
                  {affName(ev.affiliate_id) || "affiliate"}{ev.affiliate_event ? ` satellite · pays ${ev.paid_places || (ev.affiliate_codes || []).length} · ${(ev.affiliate_codes || []).length} codes` : ""}</span>}
              </div>
              {ev.affiliate_event && (
                <div className="text-xs mt-2 flex flex-wrap items-center gap-3" data-testid={`admin-mtt-approval-${ev.event_id}`}>
                  {ev.affiliate_approved
                    ? <span className="rounded-full border border-emerald-400/60 px-2 py-0.5 text-[10px] tracking-[0.15em] text-emerald-200">AFFILIATE APPROVED {ev.affiliate_approved_at ? new Date(ev.affiliate_approved_at).toLocaleDateString() : ""}</span>
                    : <span className="rounded-full border border-amber-400/60 px-2 py-0.5 text-[10px] tracking-[0.15em] text-amber-200">AWAITING AFFILIATE SIGN-OFF · hidden from the lobby</span>}
                  {ev.affiliate_note && <span className="text-slate-300 italic" data-testid={`admin-mtt-aff-note-${ev.event_id}`}>“{ev.affiliate_note}”</span>}
                  {(ev.affiliate_codes || []).length > 0 && (
                    <span className="font-mono text-[11px] text-slate-400" data-testid={`admin-mtt-aff-codes-${ev.event_id}`}>
                      {(ev.affiliate_codes || []).map((c, i) => `${ordinal(i + 1)} ${c}`).join("  ·  ")}</span>
                  )}
                </div>
              )}
              {ev.live && (
                <div className="text-xs mt-2 flex flex-wrap gap-x-4 text-amber-200/90" data-testid={`admin-mtt-live-${ev.event_id}`}>
                  <span className="flex items-center gap-1"><Timer className="w-3 h-3" /> {ev.live.status} · level {ev.live.level} · {ev.live.sb}/{ev.live.bb}</span>
                  <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {ev.live.remaining}/{ev.live.registered} · {ev.live.tables} tables</span>
                  <span>pool {fmtChips(ev.live.prize_pool)}</span>
                </div>
              )}
              {(ev.registrations?.length || 0) > 0 && (
                <div className="text-xs mt-2" data-testid={`admin-mtt-regs-${ev.event_id}`}>
                  <button onClick={() => setRegsOpen(regsOpen === ev.event_id ? null : ev.event_id)}
                    data-testid={`admin-mtt-regs-toggle-${ev.event_id}`} className="text-amber-200/90 hover:text-amber-100 underline decoration-dotted">
                    {ev.registrations.filter((r) => r.status === "registered").length} registered now · {ev.registrations.length} on record
                  </button>
                  {regsOpen === ev.event_id && (
                    <div className="mt-2 flex flex-col gap-1 font-mono text-[11px] text-slate-300 max-h-56 overflow-auto">
                      {ev.registrations.map((r) => (
                        <div key={`${r.user_id}-${r.scheduled_at}`} data-testid={`admin-mtt-reg-${r.user_id}-${r.scheduled_at}`} className="flex gap-3">
                          <span className="text-amber-100 w-40 truncate">{r.name}</span>
                          <span className="w-44 text-slate-400">{new Date(r.scheduled_at).toLocaleString()}</span>
                          <span className={r.status === "registered" ? "text-emerald-300" : "text-slate-500"}>{r.status}</span>
                          <span className="text-slate-500">{r.registered_at ? new Date(r.registered_at).toLocaleTimeString() : ""}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button data-testid={`admin-mtt-edit-${ev.event_id}`} onClick={() => edit(ev)}
                className="rounded-full px-3 py-1 text-[11px] bg-white/5 hover:bg-amber-400/25 flex items-center gap-1"><Pencil className="w-3 h-3" /> {form?._edit === ev.event_id ? "Close editor" : "Edit"}</button>
              <button data-testid={`admin-mtt-open-${ev.event_id}`} onClick={() => hit(ev, C.ApiRoutes.ADMIN_MTT_OPEN_NOW, "Registration open", { minutes: 3 })}
                className="rounded-full px-3 py-1 text-[11px] bg-white/5 hover:bg-emerald-500/25 flex items-center gap-1"><Play className="w-3 h-3" /> Open now</button>
              <button data-testid={`admin-mtt-start-${ev.event_id}`} onClick={() => hit(ev, C.ApiRoutes.ADMIN_MTT_START_NOW, "Tournament started")}
                className="rounded-full px-3 py-1 text-[11px] bg-white/5 hover:bg-amber-500/25">Start now</button>
              <button data-testid={`admin-mtt-cancel-${ev.event_id}`} onClick={() => hit(ev, C.ApiRoutes.ADMIN_MTT_CANCEL, "Cancelled — everyone refunded")}
                className="rounded-full px-3 py-1 text-[11px] bg-white/5 hover:bg-red-500/25 flex items-center gap-1"><Ban className="w-3 h-3" /> Cancel</button>
              <button data-testid={`admin-mtt-bots-${ev.event_id}`} onClick={() => patch(ev, { bots: !ev.bots, bot_count: ev.bots ? 0 : Math.max(ev.bot_count || 0, 11) })}
                className="rounded-full px-3 py-1 text-[11px] bg-white/5 hover:bg-white/15">{ev.bots ? "Bots off" : "Bots on"}</button>
              <button data-testid={`admin-mtt-resend-${ev.event_id}`} onClick={() => hit(ev, "/api/admin/mtt/{event_id}/request-approval", "Sent back to the affiliate for sign-off")}
                className={`rounded-full px-3 py-1 text-[11px] bg-white/5 hover:bg-white/15 ${ev.affiliate_event ? "" : "hidden"}`}>Send for sign-off</button>
              <button data-testid={`admin-mtt-status-${ev.event_id}`} onClick={() => patch(ev, { status: ev.status === "open" ? "closed" : "open" })}
                className="rounded-full px-3 py-1 text-[11px] bg-white/5 hover:bg-white/15">{ev.status === "open" ? "Close" : "Re-open"}</button>
            </div>
          </div>
        ))}
        {rows.length === 0 && <div className="text-sm text-slate-400" data-testid="admin-mtt-empty">No tournaments configured yet.</div>}
      </div>
    </div>
  );
};
